<template>
  <div class="module">
    <Spinner v-if="getLoading"></Spinner>

    <transition name="slide-fade">
      <div class="charts panel" v-if="!getLoading">
        <div class="filters">
          <a
            class="filter"
            @click.prevent="filterUpdate(7, true)"
            href="#"
            :class="{ active: dayFilter === 7 && !customSearch }"
            >Week</a
          >

          <a
            class="filter"
            @click.prevent="filterUpdate(30, true)"
            href="#"
            :class="{ active: dayFilter === 30 && !customSearch }"
            >Month</a
          >

          <a
            class="filter"
            href="#"
            @click="toggleCustomDateFilter"
            :class="{ active: customSearch }"
            >Custom</a
          >
        </div>

        <!-- Custom date filter -->
        <form
          class="custom-filter"
          v-if="showCustomDateFilter"
          @submit.prevent="filterCustomDates(true)"
        >
          <label for="custom-start-date">
            <span>Start date</span>
            <input
              type="date"
              name="custom-start-date"
              id="custom-start-date"
              v-model="customStartDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <label for="custom-end-date">
            <span>End date</span>
            <input
              type="date"
              name="custom-end-date"
              id="custom-end-date"
              v-model="customEndDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <input class="button button--standard" type="submit" value="Go" />
        </form>

        <div class="date-change" v-if="!customSearch">
          <a
            @click.prevent="previousDates(true)"
            class="previous-dates"
            href="#"
          >
            <img
              :src="require('@/assets/img/arrow.svg')"
              alt="previous date set"
            />
          </a>

          <span
            >{{ formatDate(filterStartDate) }} -
            {{ formatDate(filterEndDate) }}</span
          >

          <a
            @click.prevent="nextDates(true)"
            class="next-dates"
            href="#"
            v-if="!isFilterEndToday"
          >
            <img :src="require('@/assets/img/arrow.svg')" alt="next date set" />
          </a>
        </div>
      </div>
    </transition>

    <transition name="slide-fade">
      <div class="entries" v-if="!getLoading">
        <details
          v-for="(day, index) in days"
          :key="index"
          :class="{ entry: day.entry }"
        >
          <summary>
            {{ formatDate(day.date) }}
            <span class="need-log" v-if="!day.entry"
              >Log your {{ module | unMachine }}</span
            >
            <Arrow class="arrow" :color="day.entry ? '#000' : '#fff'"></Arrow>
          </summary>

          <div class="details-content">
            <component
              :is="currentModuleTable"
              :entry="day.entry"
              v-if="day.entry && !addNewEntry"
            ></component>

            <!-- Load in the form for the module if there isn't an entry -->
            <component
              :is="currentModuleForm"
              @form-submit="formSubmit"
              @cancel-submit="cancelSubmit"
              :existingEntry="day.entry"
              :day="day"
            ></component>
          </div>
        </details>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";

import Spinner from "@/components/Spinner.vue";
import Arrow from "@/components/svg/Arrow.vue";

import Utility from "@/mixins/utility";

import { subDays } from "date-fns";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/auth";

export default {
  name: "Behavior",

  mixins: [Utility],

  components: { Spinner, Arrow },

  async created() {
    // Update title immediately when component is created using route params directly
    const moduleName = this.$route.params.module;
    if (moduleName) {
      this.changePageTitle(moduleName);
      this.changePageIntroText("");
    }

    // Check for user.
    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        // Set the user id,
        this.setUID(user.uid);

        // Track module access for research analytics (Requirement IDs: 1, 3)
        const { trackModuleAccess } = await import("@/utils/analytics");
        trackModuleAccess("behavior", this.module, user.uid);

        // Set loading to true.
        this.modifyLoading(true);

        // Set logged in to true.
        this.modifyLoggedIn(true);

        // Grabs entries and days.
        this.updateDays(
          7,
          this.filterStartDate,
          this.filterEndDate,
          this.module,
          true
        );

        // Disable loader.
        this.disableLoader(1000);
      } else {
        // Send the user to the auth page if they aren't logged in.
        this.$router.push("/auth");
      }
    });
  },

  // Watch for route changes to update title
  watch: {
    $route(to, from) {
      // Update title when route parameter changes
      if (to.params.module !== from.params.module) {
        this.changePageTitle(to.params.module);
      }
    },
    // Also watch the computed module property
    module(newModule) {
      this.changePageTitle(newModule);
    },
  },

  methods: {
    ...mapMutations([
      "changePageTitle",
      "changePageIntroText",
      "modifyLoggedIn",
    ]),

    // Handle form submit.
    formSubmit(data, day) {
      this.addNewEntry = false;

      // Create a new entry.
      this.createLogEntry(this.module, data, day.date);
    },

    cancelSubmit() {
      this.addNewEntry = false;
    },
  },

  computed: {
    ...mapGetters(["getLoading"]),

    // Imports the matching module form fields.
    currentModuleForm() {
      return () => import(`../components/behavior/${this.module}/Form.vue`);
    },

    currentModuleTable() {
      return () => import(`../components/behavior/${this.module}/Table.vue`);
    },
  },
};
</script>
